import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { SectionHeading } from './SectionHeading';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { thumbUrl } from '../lib/api';

const RECENT_DAYS = 7;
const isRecent = (c) => c.created_at && Date.now() - new Date(c.created_at).getTime() < RECENT_DAYS * 86400000;

const TopTenRow = ({ deals, title = "Top 10 des catalogues aujourd’hui : La Réunion" }) => {
  const navigate = useNavigate();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const scroller = useRef(null);

  const updateArrows = () => {
    const el = scroller.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [deals]);

  if (!deals || deals.length === 0) return null;

  const scrollBy = (dir) => scroller.current?.scrollBy({ left: dir * scroller.current.clientWidth * 0.8, behavior: 'smooth' });

  const Arrow = ({ dir, visible }) => (
    <div className={`absolute top-0 bottom-0 z-20 w-24 flex items-center pointer-events-none transition-opacity duration-300 ${dir < 0 ? 'left-0 justify-start bg-gradient-to-r' : 'right-0 justify-end bg-gradient-to-l'} from-black/60 via-black/20 to-transparent ${visible ? 'opacity-0 group-hover/row:opacity-100' : 'opacity-0'}`}>
      <button onClick={() => scrollBy(dir)} aria-label={dir < 0 ? 'Précédent' : 'Suivant'} data-testid={dir < 0 ? 'top10-prev' : 'top10-next'} tabIndex={visible ? 0 : -1}
        className={`pointer-events-auto w-11 h-11 mx-2 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white flex items-center justify-center shadow-xl transition-transform hover:scale-110 hover:bg-[#E50914] hover:border-[#E50914] ${visible ? '' : 'invisible'}`}>
        {dir < 0 ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
      </button>
    </div>
  );

  return (
    <section className="py-6 relative group/row" data-testid="top10-section">
      <SectionHeading testId="top10-title" className="mb-4">{t(title)}</SectionHeading>
      <div className="relative">
        <Arrow dir={-1} visible={canPrev} />
        <div ref={scroller} onScroll={updateArrows} className="flex gap-2 overflow-x-auto overflow-y-hidden scroll-smooth snap-x snap-proximity pt-2 pb-5 top10-scroller" style={{ overscrollBehaviorX: 'contain' }} data-testid="top10-scroller">
          {deals.map((deal, idx) => (
            <button key={deal.id} onClick={() => navigate(`/catalogue/${deal.id}`)} data-testid={`top10-item-${idx + 1}`}
              className="relative shrink-0 snap-start flex items-end w-[230px] h-[250px] md:w-[260px] md:h-[280px] pb-2 text-left group/item">
              <span aria-hidden className="top10-number absolute left-0 bottom-0 leading-none select-none pointer-events-none font-black" data-testid="top10-rank">
                {idx + 1}
              </span>
              <div className="relative ml-auto w-[130px] md:w-[150px] aspect-[2/3] transition-transform duration-300 group-hover/item:scale-105" title={deal.brand}>
                <div className="w-full h-full rounded-md overflow-hidden border border-[#262626] bg-[#1a1a1a] shadow-2xl group-hover/item:border-white/40">
                  <img src={thumbUrl(deal.images[0])} alt={deal.brand} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/70 to-transparent pointer-events-none rounded-t-md" />
                  <span className="absolute top-1.5 left-2 right-2 text-[11px] font-bold text-white truncate drop-shadow" data-testid="top10-brand">{deal.brand}</span>
                </div>
                {isRecent(deal) && (
                  <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#E50914] text-white text-[11px] font-bold px-2.5 py-1 rounded-sm shadow-lg whitespace-nowrap" data-testid="top10-recent-badge">
                    {t('Ajouté récemment')}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
        <Arrow dir={1} visible={canNext} />
      </div>
    </section>
  );
};

export default TopTenRow;
